/* Config for the short submission workflows (Award, Certification, Event, Media).
   Each flow renders through the generic SubmitShort wizard.
   Question sets follow the spec in the Feb-2026 capture-logic brief. */

export const ENTRY_TYPES = {
  project: { key: "project", label: "Project", short: "Detailed case-study workflow", icon: "FileText" },
  award: { key: "award", label: "Award", short: "Capture awards & shortlists", icon: "Trophy" },
  certification: { key: "certification", label: "Certification / Accreditation", short: "ISO and accreditations", icon: "ShieldCheck" },
  event: { key: "event", label: "Event", short: "Conferences, dinners, exhibitions", icon: "Calendar" },
  media: { key: "media", label: "Media Coverage & News", short: "Press, articles, social", icon: "Newspaper" },
  photos_first: { key: "photos_first", label: "Photos First", short: "Fast on-site capture", icon: "Camera" },
};

const SUBMITTED_BY_STEP = {
  title: "Submitted by",
  hint: "So we know who to credit and contact about this submission.",
  fields: [
    { key: "submitter_name", label: "Your name", required: true, placeholder: "e.g. Sam Taylor" },
    { key: "submitter_email", label: "Your email address", required: true, type: "email", placeholder: "you@company.com" },
  ],
};

const FILES_STEP = (label = "Files", note = "Upload supporting documents, screenshots, photos.") => ({
  title: label,
  hint: note,
  upload: true,
  fields: [
    { key: "type_notes", label: "Notes", type: "textarea", placeholder: "Anything we should know about the files?" },
  ],
});

/* ---------- Award ---------- */
export const AWARD_FLOW = {
  type: "award",
  label: "Award",
  emailLabel: "Award",
  steps: [
    SUBMITTED_BY_STEP,
    {
      title: "Award details",
      eyebrow: "Step 1",
      fields: [
        { key: "type_data.award_name", label: "Award name", required: true, placeholder: "e.g. Highways Awards 2026" },
        { key: "type_data.organisation", label: "Organiser", placeholder: "e.g. CIHT" },
        { key: "type_data.category", label: "Category", placeholder: "e.g. Project Team of the Year" },
        { key: "type_data.year", label: "Year", placeholder: "e.g. 2026" },
        { key: "type_data.outcome", label: "Result", type: "select", options: ["Winner", "Highly Commended", "Finalist", "Shortlisted", "Submitted", "Pending"] },
        { key: "type_data.related_project", label: "Project, team or product involved", placeholder: "Project or team name if applicable" },
      ],
    },
    {
      title: "The story",
      eyebrow: "Step 2",
      fields: [
        { key: "type_data.what_recognised", label: "What was recognised?", type: "textarea", placeholder: "The scheme, the team, the innovation, the outcomes…" },
        { key: "type_data.why_entered", label: "Why did we enter?", type: "textarea", placeholder: "What made this worth entering?" },
        { key: "type_data.key_achievements", label: "Key achievements", type: "textarea", placeholder: "The stand-out things this project or team delivered." },
        { key: "type_data.stats_results", label: "Statistics / results", type: "textarea", placeholder: "Numbers, KPIs, before/after figures, savings, tonnes, %…" },
        { key: "type_data.team_members", label: "Team members involved", type: "textarea", placeholder: "Who did the work?" },
        { key: "type_data.judges_feedback", label: "Judges feedback (if available)", type: "textarea", placeholder: "Copy any quotes or comments from the judges here." },
      ],
    },
    {
      title: "Evidence & files",
      eyebrow: "Step 3",
      upload: true,
      fields: [
        { key: "type_data.outcome_notes", label: "Additional notes", type: "textarea", placeholder: "Certificates, photos, logos, links…" },
      ],
    },
  ],
};

/* ---------- Certification / Accreditation ---------- */
export const CERT_FLOW = {
  type: "certification",
  label: "Certification / Accreditation",
  emailLabel: "Certification",
  steps: [
    SUBMITTED_BY_STEP,
    {
      title: "Certification details",
      eyebrow: "Step 1",
      fields: [
        { key: "type_data.certification_name", label: "Certification name", required: true, placeholder: "e.g. ISO 14001" },
        { key: "type_data.issuing_organisation", label: "Awarding organisation", placeholder: "e.g. BSI" },
        { key: "type_data.date_achieved", label: "Date achieved", type: "date" },
        { key: "type_data.renewal_date", label: "Expiry / renewal date", type: "date" },
      ],
    },
    {
      title: "What it proves",
      eyebrow: "Step 2",
      fields: [
        { key: "type_data.what_shows", label: "What does this certification prove?", type: "textarea" },
        { key: "type_data.why_important", label: "Why is it important?", type: "textarea" },
        { key: "type_data.supports", label: "Which services does it support?", type: "textarea", placeholder: "e.g. Highways, Structures, Drainage" },
        { key: "type_data.compliance_benefits", label: "Compliance benefits", type: "textarea", placeholder: "How does this help us meet standards / obligations?" },
        { key: "type_data.client_benefits", label: "Client benefits", type: "textarea", placeholder: "What does the client get out of us holding this?" },
      ],
    },
    FILES_STEP("Upload certificate", "Attach the certificate PDF and any supporting evidence."),
  ],
};

/* ---------- Event ---------- */
export const EVENT_FLOW = {
  type: "event",
  label: "Event",
  emailLabel: "Event",
  steps: [
    SUBMITTED_BY_STEP,
    {
      title: "Event details",
      eyebrow: "Step 1",
      fields: [
        { key: "type_data.event_name", label: "Event name", required: true, placeholder: "e.g. Highways UK" },
        { key: "type_data.event_type", label: "Type", type: "select",
          options: ["Conference", "Exhibition", "Charity", "Team event", "Training", "Networking", "Awards Dinner", "Client Event", "Industry Event", "Other"] },
        { key: "type_data.location", label: "Location", placeholder: "e.g. NEC Birmingham" },
        { key: "type_data.date", label: "Date", type: "date" },
      ],
    },
    {
      title: "The story",
      eyebrow: "Step 2",
      fields: [
        { key: "type_data.who_attended", label: "Who attended?", type: "textarea", placeholder: "People who represented us." },
        { key: "type_data.why_attended", label: "Why did we attend?", type: "textarea", placeholder: "What was the purpose / opportunity?" },
        { key: "type_data.key_moments", label: "Key moments", type: "textarea", placeholder: "Stand-out moments, talks, meetings, wins." },
        { key: "type_data.people_involved", label: "People involved", type: "textarea", placeholder: "Clients, partners, sub-contractors, sponsors." },
        { key: "type_data.outcomes", label: "Outcomes", type: "textarea", placeholder: "New contacts, ideas, business dev, learnings." },
      ],
    },
    {
      title: "Photos, video & links",
      eyebrow: "Step 3",
      upload: true,
      fields: [
        { key: "type_data.outcome_notes", label: "Anything else worth capturing?", type: "textarea" },
      ],
    },
  ],
};

/* ---------- Media Coverage & News ---------- */
export const MEDIA_FLOW = {
  type: "media",
  label: "Media Coverage & News",
  emailLabel: "Media Coverage & News",
  steps: [
    SUBMITTED_BY_STEP,
    {
      title: "Coverage details",
      eyebrow: "Step 1",
      fields: [
        { key: "type_data.title", label: "Title", required: true, placeholder: "e.g. Company wins new contract" },
        { key: "type_data.publication", label: "Publication / source", placeholder: "e.g. New Civil Engineer" },
        { key: "type_data.date", label: "Date", type: "date" },
        { key: "type_data.url", label: "Link", placeholder: "https://" },
        { key: "type_data.related_project", label: "Related project (optional)" },
        { key: "type_data.coverage_type", label: "Coverage type", type: "select",
          options: ["Press Release", "Company News", "Project News", "Contract Win", "Award News", "Industry Article", "Magazine Feature", "Website Article", "Podcast Feature", "Video Feature", "Client Feature", "Social Media Coverage", "Other"] },
      ],
    },
    {
      title: "The story",
      eyebrow: "Step 2",
      fields: [
        { key: "type_data.about", label: "What was covered?", type: "textarea" },
        { key: "type_data.why_important", label: "Why is it important?", type: "textarea" },
        { key: "type_data.featured", label: "People / projects mentioned", type: "textarea" },
        { key: "type_data.key_messages", label: "Key messages", type: "textarea", placeholder: "Headlines and phrases we want to remember + reuse." },
      ],
    },
    FILES_STEP("Screenshots & files", "Upload screenshots, PDFs, images, video — or paste links in Notes."),
  ],
};

export const FLOWS = {
  award: AWARD_FLOW,
  certification: CERT_FLOW,
  event: EVENT_FLOW,
  media: MEDIA_FLOW,
};
